import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * Predictive search.
 * VELUNE PERFORMANCE V2:
 * - no speculative recently-viewed request on page load;
 * - recently viewed is hydrated only when search opens;
 * - live search requests are truly abortable;
 * - closing an already-empty search does not re-fetch/re-morph it;
 * - controllers are safe across disconnect/reconnect.
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  /** @type {AbortController | null} */
  #controller = null;

  /** @type {AbortController | null} */
  #activeFetch = null;

  #emptyStateLoaded = false;
  #resultsActive = false;
  #isKeyboardNavigation = false;

  get dialog() {
    return /** @type {DialogComponent | null} */ (this.closest('dialog-component'));
  }

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) this.#showResetButton();

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
      this.addEventListener('click', this.#handleModalClick, { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#controller = null;
    this.#activeFetch?.abort();
    this.#activeFetch = null;
    this.search.cancel?.();
    this.resetSearch.cancel?.();
  }

  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button, a, input');

    if (!isInteractiveElement) this.refs.searchInput?.focus();
  };

  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') this.dialog?.toggleDialog();
  };

  #handleDialogClose = () => {
    this.#resetSearch(false);
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.#resetSearch(true);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    return /** @type {HTMLElement[]} */ (
      containers
        .flatMap((container) => {
          if (container.classList.contains('predictive-search-results__wrapper-products')) {
            return Array.from(container.querySelectorAll('.predictive-search-results__card'));
          }
          return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
        })
        .filter((item) => item instanceof HTMLElement)
    );
  }

  get #currentIndex() {
    return this.#allResultsItems.findIndex((item) => item.getAttribute('aria-selected') === 'true');
  }

  set #currentIndex(index) {
    const items = this.#allResultsItems;
    if (!items.length) return;

    let activeItem = null;

    for (const item of items) item.classList.remove('keyboard-focus');

    for (const [itemIndex, item] of items.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) item.classList.add('keyboard-focus');
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems[this.#currentIndex];
  }

  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch(false);
      return;
    }

    const items = this.#allResultsItems;
    if (!items.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;

    const currentIndex = this.#currentIndex;
    const totalItems = items.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;
      case 'Tab':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = event.shiftKey
          ? currentIndex > 0 ? currentIndex - 1 : totalItems - 1
          : currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;
      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;
      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  clearRecentlyViewedProducts(event) {
    event.stopPropagation();
    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;
    const elements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];
    if (!elements.length) return;

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');
      onAnimationEnd(recentlyViewedWrapper, () => recentlyViewedWrapper.remove());
    }
  }

  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) this.refs.searchInput.focus();
    this.#resetSearch(false);
  }, 100);

  search = debounce((event) => {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch(false);
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('section_id', this.dataset.sectionId);

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    try {
      const response = await fetch(url, {
        signal: abortController.signal,
        headers: { Accept: 'text/html' },
      });

      if (!response.ok) throw new Error(`Predictive search failed (${response.status})`);

      const resultsMarkup = await response.text();
      if (abortController.signal.aborted || !resultsMarkup || !this.isConnected) return;

      morph(predictiveSearchResults, resultsMarkup);
      this.#resultsActive = true;
      this.#resetScrollPositions();
    } catch (error) {
      if (error?.name !== 'AbortError') console.error('Predictive search error:', error);
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }

  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (!viewedProducts.length) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, true, url);
  }

  #hideResetButton() {
    this.refs.resetButton.hidden = true;
  }

  #showResetButton() {
    this.refs.resetButton.hidden = false;
  }

  #createAbortController() {
    this.#activeFetch?.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    return abortController;
  }

  async #resetSearch(force = false) {
    const { predictiveSearchResults, searchInput } = this.refs;
    const hadInput = searchInput.value.trim().length > 0;

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();
    this.#activeFetch?.abort();
    this.#activeFetch = null;

    // If the modal is already showing its empty state, there is nothing to
    // download or morph again just because the dialog closed.
    if (!force && !hadInput && !this.#resultsActive) return;

    const emptySectionId = 'predictive-search-empty';
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    try {
      const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, true, url);
      if (!this.isConnected) return;

      const parsedEmptySectionMarkup = new DOMParser()
        .parseFromString(emptySectionMarkup, 'text/html')
        .querySelector('.predictive-search-empty-section');

      if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

      const viewedProducts = RecentlyViewed.getProducts();

      if (viewedProducts.length > 0) {
        const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
        if (!recentlyViewedMarkup || !this.isConnected) return;

        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');

        if (recentlyViewedProductsHtml) {
          for (const child of recentlyViewedProductsHtml.children) {
            if (child instanceof HTMLElement) child.setAttribute('ref', 'recentlyViewedWrapper');
          }

          const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
          collectionElement?.prepend(...recentlyViewedProductsHtml.children);
        }
      }

      morph(predictiveSearchResults, parsedEmptySectionMarkup);
      this.#resultsActive = false;
      this.#emptyStateLoaded = true;
      this.#resetScrollPositions();
    } catch (error) {
      console.error('Unable to reset predictive search:', error);
    }
  }
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
